import type {DynamicLabels} from 'src/types/dynamic-labels';
import type {EntityType} from 'src/types/ats-entities';
import type {Validation} from 'src/utils/validation';
import type {
  EditableGraph,
  UseCase,
  NodeVariable,
  NodeVariables,
} from 'src/types/chatbot';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Editor,
  EditorState,
  SelectionState,
  Modifier,
  convertToRaw,
  convertFromRaw,
  CompositeDecorator,
  ContentState,
  ContentBlock,
  ContentState as ContentStateType,
} from 'draft-js';

// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/RawDraftContentState'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/RawDraftContentState.js' implicitly has an 'any' type.
import type {RawDraftContentState} from 'draft-js/lib/RawDraftContentState';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/DraftDecoratorType'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/DraftDecoratorType.js' implicitly has an 'any' type.
import type {DraftDecoratorType} from 'draft-js/lib/DraftDecoratorType';
import {useDispatch} from 'react-redux';

import {insertText, type BlockNodeKey} from 'src/utils/draft';
import linkify from 'src/utils/linkify';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';

import {
  addGif,
  addDynamicLabel,
  dynamicLabelDecorator,
  getMeEditorStateWithDynamicLabelsAsEntities,
} from 'src/components/lib/markdown-editor/dynamic-text';
import {colorBorderPrimary} from '@spaced-out/ui-design-system/lib/styles/variables/_color';
import {updateNodeVariableReferences} from 'src/components/conversation-builder/utils';
import classify from 'src/utils/classify';
import {pushModal} from 'src/action-creators/modal';
import {FormPair} from './form';
import {getOptOutMessage, OptOutMessage} from './common';
import {useSmsContext} from './sms-context';
import {usePointerDrag} from 'src/hooks/usePointerDrag';
import {useChatbotNodeVariables, useReleaseFlag} from 'src/hooks/product-flags';
import {CharacterCount} from 'src/components/lib/character-count/character-count';
import {useChatbotVariables} from './node-editor/reducer';
import {useChatbotStateContext} from './chatbot-state-context';

import EmojiDialog from 'src/components/lib/emoji-picker/dialog';
import GifDialog from 'src/components/lib/gif-picker/dialog';
import {UnstyledButton} from 'src/components/lib/new-button';
import VariableIcon from 'src/images/icons/variable-icon.svg';
import EmojiIcon from 'src/images/icons/emoji-icon.svg';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import Warning from 'src/components/conversation-builder/lib/warning/warning';
import isEmpty from 'lodash/isEmpty';

import css from './draft-editor.css';


type DraftEditorProps = {
  className?: string;
  value: EditorState;
  onChange: (arg1: EditorState) => void;
  onBlur?: (arg1: React.SyntheticEvent) => void;
  placeholder?: string;
  readOnly?: boolean;
  entityType?: EntityType;
  dynamicLabels: DynamicLabels;
  urlParams?: DynamicLabels;
  isOneLine?: unknown;
  contentSuffix?: React.ReactNode;
  error?: unknown;
  useCase?: UseCase;
  onOpenNodeVariablePicker?: (arg1: React.SyntheticEvent) => void;
  showNodeVariables?: boolean;
  isSms?: boolean;
  allowGifs?: boolean;
  allowAtsVariables?: boolean;
  allowURLParams?: boolean;
  allowEmojis?: boolean;
  triggerRef?: React.Ref<'div'>;
};

export function DraftEditor({
  className,
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly,
  entityType,
  dynamicLabels,
  urlParams = [],
  isOneLine,
  contentSuffix,
  error,
  useCase,
  onOpenNodeVariablePicker,
  showNodeVariables,
  isSms,
  allowGifs = true,
  allowAtsVariables = true,
  allowEmojis = true,
  allowURLParams = false,
  triggerRef,
}: DraftEditorProps) {
  const {t, makeKey} = useLabelTranslation();
  const dispatch = useDispatch();
  const chatbotGiphyEnabled =
    useReleaseFlag('chatbotGiphyEnabled') && allowGifs;
  const handleVariableButtonClick = () => {
    if (entityType) {
      dispatch(
        pushModal({
          type: 'VARIABLE_PICKER',
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'labels' does not exist in type 'BasicModal'.
          labels: dynamicLabels,
          // @ts-expect-error - TS7006 - Parameter 'dynamicLabel' implicitly has an 'any' type.
          insertResult: (dynamicLabel) => {
            onChange(
              addDynamicLabel(value, value.getSelection(), dynamicLabel),
            );
          },
          entityType,
        }),
      );
    }
  };

  const handleURLParamButtonClick = () => {
    if (entityType) {
      dispatch(
        pushModal({
          type: 'VARIABLE_PICKER',
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'labels' does not exist in type 'BasicModal'.
          labels: urlParams,
          // @ts-expect-error - TS7006 - Parameter 'urlParam' implicitly has an 'any' type.
          insertResult: (urlParam) => {
            onChange(addDynamicLabel(value, value.getSelection(), urlParam));
          },
          entityType,
        }),
      );
    }
  };

  const handleGifButtonClick = () => {
    dispatch(
      pushModal({
        render: ({removeModal}) => (
          <GifDialog
            onClose={removeModal}
            onSelect={(gif) => {
              // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'GifData'.
              onChange(addGif(value, value.getSelection(), gif));
            }}
          />
        ),
      }),
    );
  };

  const handleEmojiButtonClick = () => {
    dispatch(
      pushModal({
        render: ({removeModal}) => (
          <EmojiDialog
            onClose={removeModal}
            onSelect={(emoji) => {
              onChange(insertText(value, emoji));
            }}
            t={t}
            makeKey={makeKey}
          />
        ),
      }),
    );
  };

  const [height, setHeight] = React.useState<number | null | undefined>();
  const [resizeHandleProps] = usePointerDrag({
    onMove: ({deltaY}) => {
      const {current} = rootRef;
      if (current) {
        // @ts-expect-error - TS2339 - Property 'offsetHeight' does not exist on type '{}'.
        setHeight(current.offsetHeight + deltaY);
      }
    },
  });
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const rootRef = React.useRef();
  React.useEffect(() => {
    // @ts-expect-error - TS2339 - Property 'offsetHeight' does not exist on type '{}'.
    setHeight(rootRef.current?.offsetHeight || 0);
  }, []);

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const editorRef = React.useRef();
  const showEntityVariablePicker = useCase !== 'sourcing' && allowAtsVariables;
  const nodeVariablesEnabled = useChatbotNodeVariables();
  const showNodeVariablePicker =
    (!showEntityVariablePicker || nodeVariablesEnabled) && showNodeVariables;

  return (
    <div
      className={classify(
        isOneLine && css.oneLine,
        css.root,
        className,
        error && css.withError,
      )}
    >
      <div
        className={classify(
          css.scroll,
          isOneLine && css.oneLine,
          readOnly && css.readOnly,
        )}
        // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
        ref={rootRef}
        // @ts-expect-error - TS2322 - Type 'number | null | undefined' is not assignable to type 'Height<string | number> | undefined'.
        style={{height}}
        // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
        onClick={() => editorRef.current?.focus()}
      >
        <div className={css.text}>
          {/* @ts-expect-error - TS2769 - No overload matches this call. | TS2786 - 'Editor' cannot be used as a JSX component. */}
          <Editor
            blockRendererFn={(contentBlock) =>
              blockRenderer(contentBlock, onChange, value)
            }
            ref={editorRef}
            editorState={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            spellCheck
            readOnly={readOnly}
            handleDrop={handled}
            handleReturn={isOneLine ? handled : undefined}
            // NOTE (kyle): we dont allow any styling but need to preserve line breaks
            // when pasting.
            //stripPastedStyles
            customStyleMap={{
              BOLD: {},
              ITALIC: {},
              UNDERLINE: {},
              CODE: {},
            }}
          />
        </div>
        <div className={classify('DraftEditor-editorContainer', css.suffix)}>
          {contentSuffix}
        </div>
      </div>
      {!readOnly && (
        <div className={css.controls}>
          {allowEmojis && (
            <UnstyledButton
              className={css.variableIcon}
              onClick={handleEmojiButtonClick}
            >
              <EmojiIcon />
            </UnstyledButton>
          )}
          {showEntityVariablePicker && (
            <UnstyledButton
              className={css.variableIcon}
              onClick={handleVariableButtonClick}
            >
              <VariableIcon />
            </UnstyledButton>
          )}
          {allowURLParams && urlParams.length > 0 && (
            <UnstyledButton
              className={css.variableIcon}
              onClick={handleURLParamButtonClick}
            >
              <Icon color="tertiary" name="code" size="medium" type="regular" />
            </UnstyledButton>
          )}
          {showNodeVariablePicker && (
            // @ts-expect-error - TS2322 - Type 'Ref<"div"> | undefined' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div ref={triggerRef}>
              <UnstyledButton
                className={css.variableIcon}
                onClick={onOpenNodeVariablePicker}
              >
                <NodeVariableIcon />
              </UnstyledButton>
            </div>
          )}
          {chatbotGiphyEnabled && (
            <UnstyledButton
              className={css.variableIcon}
              onClick={handleGifButtonClick}
            >
              {t('gif', 'gif')}
            </UnstyledButton>
          )}
          <div className={css.resizeHandle} {...resizeHandleProps}>
            <svg viewBox="0 0 10 10" width="10" height="10" strokeWidth="1">
              <line x1="1" y1="9" x2="9" y2="1" />
              <line x1="6" y1="9" x2="9" y2="6" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
const NodeVariableIcon = (props) => (
  <svg
    width="10"
    height="14"
    viewBox="0 0 10 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.81816 2.00011L1.81816 4.36375L8.42422 4.36375L8.42422 2.00011L1.81816 2.00011ZM1.36362 0.636475C0.861543 0.636475 0.454529 1.04349 0.454529 1.54556L0.454528 4.81829C0.454528 5.32037 0.861543 5.72738 1.36362 5.72738L4.69705 5.72738L4.69705 8.2728L5.54553 8.2728L5.54553 5.72738L8.87877 5.72738C9.38085 5.72738 9.78786 5.32037 9.78786 4.81829L9.78786 1.54556C9.78786 1.04349 9.38085 0.636474 8.87877 0.636474L1.36362 0.636475ZM1.81817 9.6365L1.81817 12.0001L8.42423 12.0001L8.42423 9.6365L1.81817 9.6365ZM1.36362 8.27286C0.861543 8.27286 0.454528 8.67987 0.454528 9.18195L0.454528 12.4547C0.454528 12.9568 0.861543 13.3638 1.36362 13.3638L8.87877 13.3638C9.38085 13.3638 9.78786 12.9568 9.78786 12.4547L9.78786 9.18195C9.78786 8.67988 9.38085 8.27286 8.87877 8.27286L1.36362 8.27286Z"
      fill="#767676"
    />
  </svg>
);

const handled = () => 'handled';

type UncontrolledDraftEditorProps = {
  defaultValue: RawDraftContentState;
  decorator: CompositeDecorator;
  onChange: (arg1: EditorState) => unknown;
  className?: string;
  onBlur?: (arg1: React.FocusEvent<HTMLElement>) => unknown;
  placeholder?: string;
  readOnly?: boolean;
  entityType: EntityType;
  dynamicLabels: DynamicLabels;
  isOneLine?: unknown;
  contentSuffix?: React.ReactNode;
  error?: unknown;
  useCase?: UseCase;
  nodeVariables?: NodeVariables;
  isSms?: boolean;
  allowGifs?: boolean;
  allowAtsVariables?: boolean;
};

export function UncontrolledDraftEditor({
  defaultValue,
  decorator,
  onChange,
  nodeVariables,
  ...props
}: UncontrolledDraftEditorProps) {
  const [editorState, setEditorState] = React.useState(() =>
    defaultValue
      ? EditorState.createWithContent(convertFromRaw(defaultValue), decorator)
      : EditorState.createEmpty(decorator),
  );

  const handleNodeVariableButtonClick = (nodeVariable: NodeVariable) => {
    setEditorState(
      addDynamicLabel(
        editorState,
        editorState.getSelection(),
        nodeVariable,
        'NODE_VARIABLE',
      ),
    );
  };

  return (
    <ClickAway>
      {({onOpen, isOpen, triggerRef, boundaryRef}) => (
        <>
          {/* @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'. */}
          <div className={css.column} ref={triggerRef}>
            <DraftEditor
              {...props}
              value={editorState}
              onChange={setEditorState}
              onOpenNodeVariablePicker={() => {
                onOpen();
              }}
              showNodeVariables={nodeVariables && nodeVariables.length > 0}
              onBlur={() => void onChange(editorState)}
            />
          </div>
          {isOpen && nodeVariables != null && (
            <NodeVariablePicker
              nodes={nodeVariables ?? []}
              onInsert={handleNodeVariableButtonClick}
              // @ts-expect-error - TS2322 - Type 'BoundaryRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<"div"> | undefined'.
              boundaryRef={boundaryRef}
            />
          )}
        </>
      )}
    </ClickAway>
  );
}

export const buildChatbotDecorator = (
  dynamicLabels: DynamicLabels,
  isFirstSmsMessage: boolean,
): DraftDecoratorType => {
  const decorators = [
    dynamicLabelDecorator(dynamicLabels),
    badVariableDecorator,
    nodeVariableDecorator,
    hyperlinkReadOnlyDecorator,
    // gifDecorator,
  ];
  if (isFirstSmsMessage) {
    decorators.push(linkDecorator);
  }
  return new CompositeDecorator(decorators);
};

export function MessageDraftEditor({
  label,
  defaultValue,
  onChange,
  isFirstSmsMessage,
  extraCharacterCount = 0,
  nodeVariables,
  urlParams = [],
  allowURLParams = false,
  showDiscoverJobId = false,
  showDiscoverAttributes = false,
  warningMessage = '',
  showExternalVariables = false,
  ...props
}: {
  label: string;
  defaultValue: RawDraftContentState;
  onChange: (arg1: RawDraftContentState) => unknown;
  className?: string;
  onBlur?: (arg1: React.SyntheticEvent) => unknown;
  placeholder?: string;
  readOnly?: boolean;
  entityType: EntityType;
  isOneLine?: unknown;
  isFirstSmsMessage: unknown;
  extraCharacterCount?: number;
  error?: Validation | null | undefined;
  useCase?: UseCase;
  nodeVariables?: NodeVariables;
  urlParams?: DynamicLabels;
  allowGifs?: boolean;
  allowEmojis?: boolean;
  allowAtsVariables?: boolean;
  allowURLParams?: boolean;
  showDiscoverJobId?: boolean;
  showDiscoverAttributes?: boolean;
  showExternalVariables?: boolean;
  warningMessage?: string;
}) {
  const {t} = useLabelTranslation();
  const isSms = useSmsContext();
  const dynamicLabels = useChatbotVariables(props.entityType, {
    showDiscoverJobId,
    showDiscoverAttributes,
    showExternalVariables,
  });

  // TODO(marcos): move this out somehow? this should always be present though
  const {
    state: {history, historyIndex},
  } = useChatbotStateContext();
  const currentGraph = history[historyIndex];

  const decorator = React.useMemo(
    () =>
      buildChatbotDecorator(
        [...dynamicLabels, ...urlParams],
        Boolean(isFirstSmsMessage),
      ),
    [dynamicLabels, isFirstSmsMessage],
  );

  const [editorState, setEditorState] = React.useState(() => {
    if (!isEmpty(defaultValue)) {
      const upgradedEditorState = upgradePossibleEntitiesInRDCS(
        defaultValue,
        currentGraph,
        nodeVariables,
        dynamicLabels,
        decorator,
      );

      // immediately save the new editorstate back. this seems weird
      // as we don't yet even finished this local setState call
      // but we still want to make sure that clicking 'save' after
      // rendering this editor even if you don't do anything still
      // 'saves' whatever you see.
      onChange(convertToRaw(upgradedEditorState.getCurrentContent()));

      return upgradedEditorState;
    } else {
      return EditorState.createEmpty(decorator);
    }
  });

  const handleNodeVariableButtonClick = (nodeVariable: NodeVariable) => {
    const result = addDynamicLabel(
      editorState,
      editorState.getSelection(),
      nodeVariable,
      'NODE_VARIABLE',
    );
    setEditorState(result);

    onChange(prepareForServer(result, dynamicLabels));
    // TODO: Find the root cause. Context - when a composite node variable is inserted, the editor loses focus and onBlur doesn't trigger. This is basically a hack to trigger the onBlur action here as well to make sure the node variable is saved properly
  };

  let error =
    isFirstSmsMessage && hasDecoratedText(editorState, '1.0')
      ? `${t(
          'Inserting_a_link_in_the_first_message_is_not_recommended',
          'Inserting a link in the first message is not recommended',
        )}.`
      : null;

  if (props.error?.errors?.includes('BAD_VARIABLE')) {
    error = `${t(
      'Variables_highlighted_in_red_are_invalid',
      'Variables highlighted in red are invalid',
    )}.`;
  } else if (props.error?.errors?.includes('EMPTY')) {
    error = `${t('Messages_cannot_be_empty', 'Messages cannot be empty')}.`;
  } else if (props.error?.errors?.includes('DISPOSITION_VALUE_EMPTY')) {
    error = `${t(
      'Disposition_value_cannot_be_empty',
      'Disposition value cannot be empty',
    )}.`;
  } else if (props.error) {
    error = `${t('Field_cannot_be_empty', 'Field cannot be empty')}.`;
  }
  const chatCount =
    extraCharacterCount +
    (isFirstSmsMessage
      ? getOptOutMessage(t).length + blockDelimiter.length
      : 0) +
    getCharacterCount(editorState.getCurrentContent(), blockDelimiter);
  const showSmsLengthWarning = isSms && chatCount > 160;

  return (
    <FormPair label={label} TagName="div">
      <ClickAway>
        {({onOpen, isOpen, clickAway, triggerRef, boundaryRef}) => (
          <>
            <div className={css.column}>
              <DraftEditor
                {...props}
                dynamicLabels={dynamicLabels}
                allowURLParams={allowURLParams}
                urlParams={urlParams}
                value={editorState}
                onChange={setEditorState}
                onBlur={(e) => {
                  if (props.onBlur) {
                    props.onBlur(e);
                  }
                  onChange(prepareForServer(editorState, dynamicLabels));
                }}
                error={error}
                contentSuffix={isFirstSmsMessage ? <OptOutMessage /> : null}
                onOpenNodeVariablePicker={() => {
                  onOpen();
                }}
                showNodeVariables={nodeVariables && nodeVariables.length > 0}
                isSms={isSms}
                // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<"div"> | undefined'.
                triggerRef={triggerRef}
              />

              {error && <span className={css.errorMessage}>{error}</span>}
              {warningMessage && <Warning message={warningMessage} />}
            </div>
            {isOpen && (
              <div>
                <NodeVariablePicker
                  nodes={nodeVariables ?? []}
                  onInsert={(nodeVariable) => {
                    handleNodeVariableButtonClick(nodeVariable);
                    clickAway();
                  }}
                  // @ts-expect-error - TS2322 - Type 'BoundaryRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<"div"> | undefined'.
                  boundaryRef={boundaryRef}
                />
              </div>
            )}
            {showSmsLengthWarning && <SMSLengthWarning />}
          </>
        )}
      </ClickAway>
    </FormPair>
  );
}

export function SMSLengthWarning() {
  const {t} = useLabelTranslation();
  return (
    <div className={css.smsWarningText}>{`${t(
      'Character_limit_per_SMS_is_160_characters_Longer_messages_might_be_sent_as_separate_texts',
      'Character limit per SMS is 160 characters. Longer messages might be sent as separate texts',
    )}.`}</div>
  );
}

const NodeVariablePicker = ({
  nodes,
  onInsert,
  boundaryRef,
}: {
  nodes: NodeVariables;
  onInsert: (arg1: NodeVariable) => unknown;
  boundaryRef?: React.Ref<'div'>;
}) => {
  const {t, makeKey} = useLabelTranslation();
  return (
    // @ts-expect-error - TS2322 - Type 'Ref<"div"> | undefined' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
    <div className={css.nodeVariablePicker} ref={boundaryRef}>
      <h4 className={css.nodeVariableHeader}>
        {t('Select_a_node_value', 'Select a node value')}:
      </h4>
      {nodes.length > 0 && (
        <ul className={css.nodeVariableList}>
          {nodes.map((node) => (
            <li
              key={`${node.node_id}-${node.name}-${
                node.derived_field_name ?? ''
              }`}
              className={css.nodeVariableRow}
            >
              <UnstyledButton
                className={css.nodeVariable}
                onClick={(e) => onInsert(node)}
              >
                {node.name}
              </UnstyledButton>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// @ts-expect-error - TS7006 - Parameter 'editorState' implicitly has an 'any' type.
function hasDecoratedText(editorState, decoratorKey: string) {
  const treeMap = editorState.getImmutable().get('treeMap');
  if (treeMap) {
    for (const decorationList of treeMap.values()) {
      for (const decoration of decorationList) {
        if (decoratorKey === decoration.get('decoratorKey')) {
          return true;
        }
      }
    }
  }
  return false;
}

// @ts-expect-error - TS7031 - Binding element 'editorState' implicitly has an 'any' type. | TS7031 - Binding element 'offset' implicitly has an 'any' type.
function DraftCharacterCount({editorState, offset}) {
  const {t} = useLabelTranslation();
  return (
    <CharacterCount
      tooltip={`${t(
        'Suggested_maximum_length_is',
        'Suggested maximum length is',
      )} ${CHARACTER_LIMIT} ${t(
        'characters_for_SMS_If_using_variables_this_count_will_not_be_accurate',
        'characters for SMS. If using variables, this count will not be accurate',
      )}.`}
      count={
        getCharacterCount(editorState.getCurrentContent(), blockDelimiter) +
        offset
      }
      limit={CHARACTER_LIMIT}
    />
  );
}

function urlStrategy(
  contentBlock: ContentBlock,
  callback: (start: number, stop: number) => void,
) {
  const text = contentBlock.getText();

  const matches = linkify().match(text);
  if (matches) {
    for (const match of matches) {
      callback(match.index, match.lastIndex);
    }
  }
}

export const linkDecorator = {
  strategy: urlStrategy,
  component: BadVariable,
} as const;

// TODO (kyle): account for unicode?
// @ts-expect-error - TS7006 - Parameter 'contentState' implicitly has an 'any' type.
function getCharacterCount(contentState, delimiter: string) {
  let count = 0;

  const blockMap = contentState.getBlockMap();
  for (const block of blockMap.values()) {
    count += block.getText().length + delimiter.length;
  }

  count += delimiter.length * (blockMap.size - 1);

  return count;
}

const CHARACTER_LIMIT = 160;
const blockDelimiter = '\n\n';

export function prepareForServer(
  editorState: EditorState,
  dynamicLabels: DynamicLabels,
): RawDraftContentState {
  return convertToRaw(
    getMeEditorStateWithDynamicLabelsAsEntities(
      editorState,
      dynamicLabels,
    ).getCurrentContent(),
  );
}

const badVariableRegex = /<<[^>]*>>/g;
const badVariableDecorator = {
  // @ts-expect-error - TS7006 - Parameter 'contentBlock' implicitly has an 'any' type. | TS7006 - Parameter 'callback' implicitly has an 'any' type.
  strategy: (contentBlock, callback) => {
    const text = contentBlock.getText();

    let match, start;
    while ((match = badVariableRegex.exec(text)) !== null) {
      if (match) {
        start = match.index;
        callback(start, start + match[0].length);
      }
    }
  },
  component: BadVariable,
} as const;
function BadVariable({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <span className={classify(className, css.badVariable)}>{children}</span>
  );
}

const NodeVariableComponent = ({
  children,
  className,
  decoratedText,
  contentState,
  entityKey,
}: {
  className?: string;
  children: React.ReactNode;
  decoratedText: string;
  contentState: ContentStateType;
  entityKey?: string;
  blockKey: BlockNodeKey;
  start: number;
  offsetKey: string;
}) => {
  const {t} = useLabelTranslation();
  const isPossiblyEmpty = decoratedText.endsWith('(*)”');
  return (
    <span
      className={classify(className, css.nodeVariableComponent, {
        [css.warningNodeVariableComponent]: isPossiblyEmpty,
      })}
      title={
        isPossiblyEmpty
          ? `${t(
              'This_node_variable_does_not_appear_to_be_an_ancestor_of_this_variable_and_may_be_empty',
              'This node variable does not appear to be an ancestor of this variable and may be empty',
            )}`
          : undefined
      }
    >
      {children}
    </span>
  );
};

const TemplateVariableComponent = ({
  children,
  className,
}: {
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <span className={classify(className, css.nodeVariableComponent)}>
      {children}
    </span>
  );
};

const HyperlinkComponent = ({
  children,
  className,
  contentState,
  entityKey,
  blockKey,
  start,
  end,
  handleRemoveLink,
  handleEditLink,
  chatbotCustomLinkGenerationEnabled,
  ...props
}: {
  className?: string;
  children: React.ReactNode;
  contentState: ContentStateType;
  entityKey?: string;
  blockKey: string;
  start: number;
  end: number;
  handleRemoveLink: (
    arg1: string,
    arg2: number,
    arg3: number,
    arg4: string,
  ) => unknown;
  handleEditLink: (
    arg1: string,
    arg2: number,
    arg3: number,
    arg4: string | null | undefined,
    arg5: string,
    arg6: {
      [key: string]: string;
    } | null,
  ) => unknown;
  chatbotCustomLinkGenerationEnabled?: boolean;
}) => {
  const {t, makeKey} = useLabelTranslation();
  // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'DraftEntityInstance'.
  const entity = entityKey ? contentState.getEntity(entityKey).data : null;
  const url = entity?.url;
  const label = entity?.label ?? '';
  const metadata = entity?.metadata || null;

  const currentContentBlock = contentState.getBlockForKey(blockKey);
  const selectedText = currentContentBlock.getText().slice(start, end);

  const [openLinkDetail, setOpenLinkDetail] = React.useState(false);

  const closeLinkDetail = React.useMemo(
    () => () => setOpenLinkDetail(false),
    [],
  );

  React.useEffect(() => {
    window.addEventListener('click', closeLinkDetail);

    return () => {
      window.removeEventListener('click', closeLinkDetail);
    };
  }, []);

  return (
    <span
      className={css.hyperlinkContainer}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        className={classify(className, css.hyperlink)}
        // @ts-expect-error - TS2322 - Type '{ children: any; className: string; href: any; onMouseEnter: () => void; }' is not assignable to type 'DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>'.
        href={url}
        onMouseEnter={() => setOpenLinkDetail(true)}
      >
        {t(makeKey(label), label)}
        {/* {children} */}
      </span>
      {openLinkDetail && entity?.url && (
        <div className={css.linkDetail}>
          <div>
            <Icon color="secondary" name="link" size="small" type="regular" />
            <span
              className={css.linkUrl}
              onClick={() => window.open(entity.url)}
            >
              {`${entity.url}${
                metadata?.conv_id && chatbotCustomLinkGenerationEnabled
                  ? // disabled as its a query param
                    // eslint-disable-next-line @spaced-out/i18n/no-static-labels
                    '?conv_id='
                  : ''
              }`}
            </span>
          </div>
          <div>
            <span
              className={css.linkAction}
              onClick={
                entity
                  ? () =>
                      handleEditLink(blockKey, start, end, url, label, metadata)
                  : undefined
              }
            >
              {t('Edit', 'Edit')}
            </span>
            <span
              className={css.linkAction}
              onClick={
                entity
                  ? () => handleRemoveLink(blockKey, start, end, label)
                  : undefined
              }
            >
              {t('Remove', 'Remove')}
            </span>
          </div>
        </div>
      )}
    </span>
  );
};

const HyperlinkReadOnlyComponent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <span className={css.hyperlink}>{children}</span>;
};

export const nodeVariableDecorator = {
  strategy: (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentStateType,
  ) => {
    contentBlock.findEntityRanges((meta) => {
      const entityKey = meta.getEntity();
      if (!entityKey) {
        return false;
      }
      const entityType = contentState.getEntity(entityKey)?.getType();
      return entityType === 'NODE_VARIABLE';
    }, callback);
  },
  component: NodeVariableComponent,
} as const;

export const templateVariableDecorator = {
  strategy: (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentStateType,
  ) => {
    contentBlock.findEntityRanges((meta) => {
      const entityKey = meta.getEntity();
      if (!entityKey) {
        return false;
      }
      const entityType = contentState.getEntity(entityKey)?.getType();
      return (
        entityType === 'TEMPLATE_VARIABLE' ||
        entityType === 'JOB_RESULT_ATTRIBUTE' ||
        entityType === 'CHATBOT_LINK' ||
        entityType === 'DYNAMIC_LIST'
      );
    }, callback);
  },
  component: TemplateVariableComponent,
} as const;

export const hyperlinkDecorator = {
  strategy: (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentStateType,
  ) => {
    contentBlock.findEntityRanges((meta) => {
      const entityKey = meta.getEntity();
      if (!entityKey) {
        return false;
      }
      const entityType = contentState.getEntity(entityKey)?.getType();
      return entityType === 'HYPERLINK';
    }, callback);
  },
  component: HyperlinkComponent,
} as const;

export const hyperlinkReadOnlyDecorator = {
  strategy: (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentStateType,
  ) => {
    contentBlock.findEntityRanges((meta) => {
      const entityKey = meta.getEntity();
      if (!entityKey) {
        return false;
      }
      const entityType = contentState.getEntity(entityKey)?.getType();
      return entityType === 'HYPERLINK';
    }, callback);
  },
  component: HyperlinkReadOnlyComponent,
} as const;

function checkIfImageExists(url: string, callback: (arg1: boolean) => void) {
  const img = new Image();
  img.src = url;

  if (img.complete) {
    callback(true);
  } else {
    img.onload = () => {
      callback(true);
    };

    img.onerror = () => {
      callback(false);
    };
  }
}

export function GifBlock({
  block,
  contentState,
  blockProps,
}: {
  block: ContentBlock;
  contentState: ContentStateType;
  blockProps: {
    onChange: (arg1: EditorState) => void;
    editorState: EditorState;
  };
}) {
  const {t} = useLabelTranslation();
  const text = block.getText();
  const url = text.match(/\((.*?)\)/)?.[1];
  const [imageDisplay, setImageDisplay] = React.useState<any>(null);

  const blockKey = block.getKey();

  const removeGif = () => {
    const editorState = blockProps.editorState;
    let content = editorState.getCurrentContent();

    const targetRange = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: 0,
      focusKey: blockKey,
      focusOffset: 1,
    });

    content = Modifier.removeRange(content, targetRange, 'backward');
    content = Modifier.setBlockType(content, targetRange, 'unstyled');

    const newState = EditorState.push(editorState, content, 'remove-range');

    const newSelection = new SelectionState({
      anchorKey: blockKey,
      anchorOffset: 0,
      focusKey: blockKey,
      focusOffset: 0,
    });
    return EditorState.forceSelection(newState, newSelection);
  };

  React.useEffect(() => {
    if (url) {
      checkIfImageExists(url, (exists) => {
        if (exists) {
          setImageDisplay(<img src={url} className={css.gif} />);
        } else {
          console.error(`url is invalid: ${url}`);
        }
      });
    }
  }, []);

  return (
    <div
      className={css.gifContainer}
      onClick={() => blockProps.onChange(removeGif())}
    >
      {imageDisplay}
      <div className={css.remove}>{t('Remove', 'Remove')}</div>
    </div>
  );
}

// @ts-expect-error - TS7030 - Not all code paths return a value.
function blockRenderer(
  // @ts-expect-error TS2304
  contentBlock: BlockNodeRecord,
  onChange: undefined | ((arg1: EditorState) => void),
  editorState: undefined | EditorState,
) {
  const type = contentBlock.getType();
  if (type === 'atomic') {
    return {
      component: GifBlock,
      editable: false,
      props: {onChange, editorState},
    };
  } else if (type === 'horizontal-rule') {
    return {
      component: () => (
        <hr
          style={{
            height: '1px',
            border: 'none',
            backgroundColor: colorBorderPrimary,
          }}
        />
      ),
      editable: false,
    };
  }
}

export function ReadOnlyDraftEditor({
  entityType,
  isFirstSmsMessage,
  rte_json,
}: {
  entityType: EntityType;
  isFirstSmsMessage: boolean;
  rte_json: RawDraftContentState;
}) {
  const dynamicLabels = useChatbotVariables(entityType);

  const decorator = React.useMemo(
    () => buildChatbotDecorator(dynamicLabels, Boolean(isFirstSmsMessage)),
    [dynamicLabels, isFirstSmsMessage],
  );

  const contentState = isEmpty(rte_json)
    ? ContentState.createFromText('')
    : convertFromRaw(rte_json);

  const editorState = EditorState.createWithContent(contentState, decorator);

  return (
    // @ts-expect-error - TS2786 - 'Editor' cannot be used as a JSX component.
    <Editor
      // @ts-expect-error - TS2769 - No overload matches this call.
      blockRendererFn={blockRenderer}
      editorState={editorState}
      readOnly={true}
      onChange={() => {}}
    />
  );
}

export function upgradePossibleEntitiesInRDCS(
  rdcs: RawDraftContentState,
  currentGraph: EditableGraph,
  nodeVariables: NodeVariables | null | undefined,
  dynamicLabels: DynamicLabels,
  decorator: DraftDecoratorType,
): EditorState {
  const initialContentState = isEmpty(rdcs)
    ? ContentState.createFromText('')
    : convertFromRaw(rdcs);

  // TODO(marcos): This happens on editor mount but maybe it should
  // be moved to the reducer???
  const contentState = updateNodeVariableReferences(
    initialContentState,
    currentGraph,
    // @ts-expect-error - TS2345 - Argument of type 'NodeVariables | null | undefined' is not assignable to parameter of type 'NodeVariables | undefined'.
    nodeVariables,
  );

  const editorState = EditorState.createWithContent(contentState, decorator);

  // update old entities and their labels here as well
  const updatedEditorState = getMeEditorStateWithDynamicLabelsAsEntities(
    editorState,
    dynamicLabels,
  );

  return updatedEditorState;
}
